import React, { useEffect, useRef, useState } from "react";
import { LOCALES, LOCALE_NAMES, LOCALE_SHORT } from "./config";
import { useLocale, useT } from "./context";

/**
 * Minimal language dropdown for the nav. Shows the current language as a compact
 * label; opens a small popover listing all languages. Colors follow the nav
 * theme so it sits quietly beside the theme toggle.
 */
export function LanguageSwitcher({ theme }: { theme: "dark" | "light" }) {
  const { locale, setLocale } = useLocale();
  const t = useT();
  const [open, setOpen] = useState(false);
  // `mounted` keeps the menu in the DOM through its exit animation; `shown`
  // drives the visible state the per-item transitions react to.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const STAGGER = 45; // ms between each item
  const EXIT_MS = (LOCALES.length - 1) * STAGGER + 180;

  const linkColor = theme === "dark" ? "rgba(255,255,255,0.7)" : "var(--text-muted)";
  const linkHover = theme === "dark" ? "#fff" : "var(--text-primary)";

  // Open: mount, then flip `shown` on the next frame so the items transition in
  // (top-down). Close: flip `shown` off (items transition out, bottom-up), then
  // unmount once the staggered exit has finished.
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [open, EXIT_MS]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t.common.aria.language}
        aria-haspopup="menu"
        aria-expanded={open}
        className="text-sm transition-colors flex items-center gap-1"
        style={{ color: open ? linkHover : linkColor, background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
        onMouseLeave={(e) => (e.currentTarget.style.color = open ? linkHover : linkColor)}
      >
        {LOCALE_SHORT[locale]}
        <svg width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden style={{ opacity: 0.5, transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }}>
          <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {mounted && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            minWidth: 96,
            padding: 0,
            background: "none",
            border: "none",
            boxShadow: "none",
            zIndex: 1000,
            pointerEvents: shown ? "auto" : "none",
          }}
        >
          {LOCALES.map((l, i) => {
            const active = l === locale;
            // In: item 0 first (top-down). Out: last item first (bottom-up).
            const delay = shown ? i * STAGGER : (LOCALES.length - 1 - i) * STAGGER;
            return (
              <button
                key={l}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { setLocale(l); setOpen(false); }}
                className="w-full text-sm text-right"
                style={{
                  display: "block",
                  padding: "3px 0",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: active ? "var(--text-primary)" : "var(--text-muted)",
                  fontWeight: active ? 500 : 400,
                  opacity: shown ? 1 : 0,
                  transform: shown ? "translateY(0)" : "translateY(-6px)",
                  transition: "opacity 180ms ease, transform 180ms ease, color 150ms ease",
                  transitionDelay: `${delay}ms`,
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "var(--text-secondary)"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "var(--text-muted)"; }}
              >
                {LOCALE_NAMES[l]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
